import { useCallback, useEffect, useRef, useState } from 'react';
import { authAPI } from '@/lib/api';
import { subscribeSession } from '@/lib/authSession';

function backendWsBase() {
  const base = process.env.REACT_APP_BACKEND_URL || '';
  if (!base) {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}`;
  }
  return base.replace('https://', 'wss://').replace('http://', 'ws://');
}

/**
 * Obtiene token WS del backend (cookie httpOnly no es legible en JS).
 * @returns {Promise<string|null>}
 */
export async function fetchWebSocketToken() {
  try {
    const res = await authAPI.wsToken();
    return res.data?.token || null;
  } catch {
    return null;
  }
}

/**
 * Hook para conectar WebSocket con token renovable.
 * @param {string|null} pathSuffix - ej. `/ws/monitor` o `/ws/chat/{id}`
 * @param {{ enabled?: boolean, onMessage?: (data: object) => void, onOpen?: () => void, onClose?: () => void }} options
 */
export function useWebSocket(pathSuffix, options = {}) {
  const { enabled = true, onMessage, onOpen, onClose } = options;
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const [connected, setConnected] = useState(false);

  const connect = useCallback(async () => {
    if (!enabled || !pathSuffix) return;

    const token = await fetchWebSocketToken();
    if (!token) {
      setConnected(false);
      return;
    }

    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch (_) { /* ignore */ }
    }

    const wsUrl = `${backendWsBase()}${pathSuffix}?token=${encodeURIComponent(token)}`;
    let ws;
    try {
      ws = new WebSocket(wsUrl);
    } catch (_) {
      setConnected(false);
      return;
    }

    ws.onopen = () => {
      setConnected(true);
      onOpen?.();
    };

    ws.onclose = (event) => {
      setConnected(false);
      onClose?.();
      if (event.code === 4001 && enabled) {
        reconnectTimer.current = setTimeout(() => connect(), 3000);
      }
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        onMessage?.(data);
      } catch (_) { /* ignore */ }
    };

    ws.onerror = () => {
      setConnected(false);
    };

    wsRef.current = ws;
  }, [enabled, pathSuffix, onMessage, onOpen, onClose]);

  useEffect(() => {
    if (!enabled || !pathSuffix) return undefined;

    connect();

    const unsub = subscribeSession((event) => {
      if (event === 'meta' && wsRef.current?.readyState === WebSocket.OPEN) {
        /* Tras refresh proactivo, reconectar con token nuevo */
        connect();
      }
    });

    return () => {
      unsub();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (_) { /* ignore */ }
      }
    };
  }, [connect, enabled, pathSuffix]);

  return { connected, reconnect: connect, wsRef };
}
